import { CaretUpDown, Check, X } from "@phosphor-icons/react"
import { cva } from "class-variance-authority"
import { omit } from "lodash-es"
import {
  type KeyboardEvent,
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"
import ReactSelect, {
  type ActionMeta,
  type ClassNamesConfig,
  type ClearIndicatorProps,
  components,
  type ControlProps,
  createFilter,
  type DropdownIndicatorProps,
  type GroupBase,
  type InputProps,
  type MultiValue,
  type MultiValueProps,
  type MultiValueRemoveProps,
  type OnChangeValue,
  type OptionProps,
  type Props as ReactSelectProps,
  type SelectInstance,
  type SingleValueProps,
  type StylesConfig,
  type ValueContainerProps,
} from "react-select"
import AsyncSelect, { type AsyncProps } from "react-select/async"
import AsyncCreatableSelect from "react-select/async-creatable"
import CreatableSelect, { type CreatableProps } from "react-select/creatable"

import { Tooltip } from "~/components/reusable/Tooltip"
import { UserAvatar } from "~/components/reusable/users/UserAvatar"
import { cn } from "~/utils/helpers"

import {
  usePermissionGate,
  type PermissionGateProps,
} from "~/components/reusable/PermissionTooltip"

export type SelectVariant = "neutral" | "ghost"
export type SelectSize = "small" | "base" | "large"
/** Standard control widths (px). `full` fills the form column. */
export type SelectWidth = 160 | 200 | 240 | 320 | "full"

export type SelectOption<T = string> = {
  label: string
  value: T
  isDisabled?: boolean
  /** Second line under the label in the menu, e.g. what the option means. Up to 2 lines; not
   * searched and not shown in the control. */
  description?: string
  /** Shown on hover over the option, e.g. why it is disabled. */
  tooltip?: string
  /** Multi only: the chip has no X and the value survives backspace and clear. */
  isFixed?: boolean
  /** User pickers: 16px avatar before the label, in the menu and in the value. */
  user?: { name: string; email: string; picture?: string }
  /** 16px icon before the label, in the menu and in the value. */
  icon?: ReactNode
}

const WIDTH_CLASS: Record<SelectWidth, string> = {
  160: "w-40",
  200: "w-50",
  240: "w-60",
  320: "w-80",
  full: "w-full",
}

type ControlState =
  | "rest"
  | "hover"
  | "open"
  | "focus"
  | "error"
  | "disabled"
  | "activeRest"
  | "activeHover"
  | "activeOpen"
  | "activeFocus"

// One complete fill + shadow per state, so no state has to out-rank another class in the CSS.
const CONTROL_SURFACE: Record<SelectVariant, Record<ControlState, string>> = {
  neutral: {
    rest: "bg-button-neutral shadow-button-neutral hover:bg-button-neutral-hover",
    hover: "bg-button-neutral-hover shadow-button-neutral",
    open: "bg-button-neutral-pressed shadow-button-neutral",
    focus: "bg-button-neutral shadow-button-neutral-focus",
    error: "bg-button-neutral shadow-borders-error hover:bg-button-neutral-hover",
    disabled: "bg-button-disabled shadow-button-neutral",
    activeRest: "bg-highlight shadow-borders-interactive-with-shadow hover:bg-highlight-hover",
    activeHover: "bg-highlight-hover shadow-borders-interactive-with-shadow",
    activeOpen: "bg-highlight-hover shadow-borders-interactive-with-shadow",
    activeFocus: "bg-highlight shadow-borders-interactive-with-focus",
  },
  // Ghost has no Active look in Figma; getControlState never returns an active state for it.
  ghost: {
    rest: "bg-button-transparent hover:bg-button-transparent-hover",
    hover: "bg-button-transparent-hover",
    open: "bg-button-transparent-pressed",
    focus: "bg-button-transparent-focus shadow-button-transparent-focus",
    error: "bg-button-transparent shadow-borders-error hover:bg-button-transparent-hover",
    disabled: "bg-button-transparent",
    activeRest: "bg-button-transparent hover:bg-button-transparent-hover",
    activeHover: "bg-button-transparent-hover",
    activeOpen: "bg-button-transparent-pressed",
    activeFocus: "bg-button-transparent-focus shadow-button-transparent-focus",
  },
}

const CONTROL_SIZE: Record<SelectSize, string> = {
  small: "h-6 px-2",
  base: "h-7 px-2",
  large: "h-8 px-2.5",
}

const controlClass = (variant: SelectVariant, size: SelectSize, state: ControlState) =>
  cn(
    "flex flex-nowrap items-center justify-between gap-1.5 rounded-full outline-none transition",
    state === "disabled" ? "cursor-not-allowed" : "cursor-pointer",
    CONTROL_SIZE[size],
    CONTROL_SURFACE[variant][state],
  )

const optionVariants = cva("flex w-full items-center gap-2 rounded-[8px] px-2 py-1.5 select-none", {
  variants: {
    size: { small: "paragraph-sm", base: "paragraph-sm", large: "paragraph-base" },
    state: {
      default: "cursor-pointer text-fg-base",
      focused: "cursor-pointer text-fg-base bg-base-hover",
      disabled: "cursor-not-allowed text-fg-disabled",
    },
  },
})

type InputModality = "pointer" | "keyboard"

// The last input the user made anywhere on the page. `:focus-visible` can't drive the ring: the
// searchable input is a text field, and browsers treat a click into a text field as focus-visible.
let modality: InputModality = "pointer"
const modalityListeners = new Set<() => void>()

const setModality = (next: InputModality) => {
  if (next === modality) return
  modality = next
  modalityListeners.forEach((listener) => listener())
}

const onPointerDown = () => setModality("pointer")
// Typing into the search box is not navigation: only keys that move or act count as keyboard use.
const onKeyDown = (event: globalThis.KeyboardEvent) => {
  if (!event.key || event.metaKey || event.ctrlKey || event.altKey) return
  if (event.key.length === 1 && event.key !== " ") return
  setModality("keyboard")
}

const subscribeModality = (listener: () => void) => {
  if (modalityListeners.size === 0) {
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown, true)
  }
  modalityListeners.add(listener)
  return () => {
    modalityListeners.delete(listener)
    if (modalityListeners.size > 0) return
    document.removeEventListener("pointerdown", onPointerDown, true)
    document.removeEventListener("keydown", onKeyDown, true)
  }
}

const useInputModality = () =>
  useSyncExternalStore(
    subscribeModality,
    () => modality,
    () => "pointer" as const,
  )

type ControlFlags = {
  isFocused: boolean
  isDisabled: boolean
  menuIsOpen: boolean
  isInvalid: boolean
  isActive: boolean
  modality: InputModality
  forceState: SelectProps<unknown, boolean>["forceState"]
}

// focus = keyboard focus only (the ring), also while a keyboard-opened menu is open. open = the
// menu opened with the pointer: pressed fill, no ring. A mouse pick leaves neither.
const getControlState = ({
  isFocused,
  isDisabled,
  menuIsOpen,
  isInvalid,
  isActive,
  modality,
  forceState,
}: ControlFlags): ControlState => {
  if (isDisabled) return "disabled"
  if (isInvalid) return "error"
  const ringed = (isFocused && modality === "keyboard") || forceState === "focus"
  const opened = menuIsOpen || forceState === "open"
  if (isActive) {
    if (ringed) return "activeFocus"
    if (opened) return "activeOpen"
    return forceState === "hover" ? "activeHover" : "activeRest"
  }
  if (ringed) return "focus"
  if (opened) return "open"
  if (forceState === "hover") return "hover"
  return "rest"
}

// Leading icon, prefix, value and caret share one colour per state.
const accentText = (isDisabled: boolean, isActive: boolean, rest: string) => {
  if (isDisabled) return "text-fg-disabled"
  if (isActive) return "text-fg-interactive"
  return rest
}

const getOptionState = (isDisabled: boolean, isFocused: boolean) => {
  if (isDisabled) return "disabled"
  if (isFocused) return "focused"
  return "default"
}

/** Props this wrapper reads back inside react-select's custom components. */
type InjectedProps = {
  leftIcon?: ReactNode
  prefix?: string
  isActive: boolean
  helperId?: string
  chipOverflow: ChipOverflow
  onChipOverflow: (overflow: ChipOverflow) => void
}

/** Multi: how many chips fit on the line; `firstMax` truncates a lone chip that doesn't fit. */
type ChipOverflow = { visible: number; firstMax: number | null }

const ALL_CHIPS: ChipOverflow = { visible: Infinity, firstMax: null }

const chipClass = (isDisabled: boolean, isActive: boolean) =>
  cn(
    "flex shrink-0 items-center gap-0.5 rounded-[6px] py-0.5 pl-1.5 pr-1 label-sm",
    isActive ? "bg-base" : "bg-badge-neutral-bg",
    isDisabled && "text-fg-disabled",
    !isDisabled && (isActive ? "text-badge-primary-fg-text" : "text-badge-neutral-fg-text"),
  )

const CHIP_GAP = 4

/** Chips are laid out greedily; a "+N" chip, if needed, must fit after the last one shown. */
const fitChips = (widths: number[], available: number, plusWidth: number): ChipOverflow => {
  let used = 0
  let visible = 0
  for (const [i, width] of widths.entries()) {
    const next = used + (visible > 0 ? CHIP_GAP : 0) + width
    const reserve = i < widths.length - 1 ? CHIP_GAP + plusWidth : 0
    if (next + reserve > available) break
    used = next
    visible++
  }
  if (visible > 0 || widths.length === 0) return { visible, firstMax: null }
  const reserve = widths.length > 1 ? CHIP_GAP + plusWidth : 0
  return { visible: 1, firstMax: Math.max(available - reserve, 0) }
}

const injected = (selectProps: unknown) => selectProps as InjectedProps

const asOption = (data: unknown) => data as Partial<SelectOption<unknown>>

const Leading = ({ data }: { data: unknown }) => {
  const { user, icon } = asOption(data)
  if (user)
    return <UserAvatar size={16} name={user.name} email={user.email} picture={user.picture} />
  if (icon) return <span className="flex text-fg-subtle [&_svg]:size-4">{icon}</span>
  return null
}

const Control = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: ControlProps<Option, IsMulti, Group>,
) => {
  const { leftIcon, prefix, isActive } = injected(props.selectProps)
  const accent = accentText(props.isDisabled, isActive, "text-fg-subtle")
  return (
    <components.Control {...props}>
      {leftIcon && <span className={cn("flex shrink-0 [&_svg]:size-4", accent)}>{leftIcon}</span>}
      {prefix && props.hasValue && (
        <span className={cn("label-base shrink-0 whitespace-nowrap", accent)}>{prefix}</span>
      )}
      {props.children}
    </components.Control>
  )
}

const SingleValue = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: SingleValueProps<Option, IsMulti, Group>,
) => (
  <components.SingleValue {...props}>
    <span className="flex min-w-0 items-center gap-1.5">
      <Leading data={props.data} />
      <span className="truncate">{props.children}</span>
    </span>
  </components.SingleValue>
)

const OptionRow = <O, IsMulti extends boolean, Group extends GroupBase<O>>(
  props: OptionProps<O, IsMulti, Group>,
) => {
  const { tooltip, description } = asOption(props.data)
  const label = description ? (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate">{props.children}</span>
      <span
        className={cn("line-clamp-2", props.isDisabled ? "text-fg-disabled" : "text-fg-subtle")}
      >
        {description}
      </span>
    </span>
  ) : (
    <span className="min-w-0 flex-1 truncate">{props.children}</span>
  )
  return (
    <components.Option {...props}>
      <Leading data={props.data} />
      {tooltip ? (
        <Tooltip
          title={tooltip}
          delayDuration={100}
          side="right"
          // react-select pins its menu portal to z-index int32 max; equal z-index lets the
          // tooltip, mounted later, paint above it by DOM order.
          className="z-[2147483647]"
        >
          {label}
        </Tooltip>
      ) : (
        label
      )}
      {props.isSelected && <Check className="size-4 shrink-0 text-fg-base" />}
    </components.Option>
  )
}

const CHIP_ATTRS = { "data-chip": "" }
const HIDDEN_CHIP_ATTRS = { "aria-hidden": true, "data-hidden": "" }

const MultiValue = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MultiValueProps<Option, IsMulti, Group>,
) => {
  const hidden = props.index >= injected(props.selectProps).chipOverflow.visible
  return (
    <components.MultiValue
      {...props}
      innerProps={{ ...props.innerProps, ...CHIP_ATTRS, ...(hidden && HIDDEN_CHIP_ATTRS) }}
    />
  )
}

const Input = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: InputProps<Option, IsMulti, Group>,
) => {
  const { helperId } = injected(props.selectProps)
  const describedBy = [props["aria-describedby"], helperId].filter(Boolean).join(" ")
  return <components.Input {...props} aria-describedby={describedBy || undefined} />
}

// Chips that don't fit stay mounted but out of flow, so their widths can still be measured.
const ValueContainer = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: ValueContainerProps<Option, IsMulti, Group>,
) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isActive, chipOverflow, onChipOverflow } = injected(props.selectProps)
  const values = props.getValue()
  const multi = props.isMulti && values.length > 0
  const hidden = multi ? values.slice(Math.min(chipOverflow.visible, values.length)) : []

  const measure = () => {
    const el = ref.current
    if (!el || !multi) return
    const chips = [...el.querySelectorAll<HTMLElement>(":scope > [data-chip]")]
    const widths = chips.map((chip) => {
      const label = chip.firstElementChild as HTMLElement | null
      return label ? chip.offsetWidth - label.clientWidth + label.scrollWidth : chip.offsetWidth
    })
    const input = el.lastElementChild as HTMLElement | null
    const inputWidth = input && !input.hasAttribute("data-chip") ? input.offsetWidth + CHIP_GAP : 0
    const plus = el.querySelector<HTMLElement>(":scope > [data-chip-measure]")
    const next = fitChips(widths, el.clientWidth - inputWidth, plus?.offsetWidth ?? 0)
    if (next.visible !== chipOverflow.visible || next.firstMax !== chipOverflow.firstMax)
      onChipOverflow(next)
  }
  // No deps: re-measure after every render (values, labels, input text); bails when unchanged.
  const measureRef = useRef(measure)
  useLayoutEffect(() => {
    measureRef.current = measure
    measure()
  })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(() => measureRef.current())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Same child slots with or without chips: moving react-select's input remounts it and drops focus.
  const [value, input] = props.children as [ReactNode, ReactNode]
  const chip = chipClass(props.isDisabled, isActive)
  return (
    <components.ValueContainer {...props} innerProps={{ ...props.innerProps, ref }}>
      {value}
      {multi && (
        <span
          aria-hidden
          data-chip-measure
          className={cn(chip, "pointer-events-none invisible absolute")}
        >
          +{values.length}
        </span>
      )}
      {hidden.length > 0 && (
        <Tooltip
          title={hidden.map((option) => props.selectProps.getOptionLabel(option)).join("\n")}
          delayDuration={100}
          // The open menu lists every pick with a check, and the tooltip must never cover it.
          disabled={props.selectProps.menuIsOpen}
          disableHoverableContent
          className="z-[2147483647]"
        >
          <span className={chip}>+{hidden.length}</span>
        </Tooltip>
      )}
      {input}
    </components.ValueContainer>
  )
}

const MultiValueRemove = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MultiValueRemoveProps<Option, IsMulti, Group>,
) => {
  if (asOption(props.data).isFixed) return null
  return (
    <components.MultiValueRemove {...props}>
      <X className="size-3 shrink-0" aria-hidden />
    </components.MultiValueRemove>
  )
}

const ClearIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: ClearIndicatorProps<Option, IsMulti, Group>,
) => (
  <components.ClearIndicator {...props}>
    <X className="size-4" aria-label="Clear" />
  </components.ClearIndicator>
)

const DropdownIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => (
  <components.DropdownIndicator {...props}>
    <CaretUpDown className="size-4" aria-hidden />
  </components.DropdownIndicator>
)

const SELECT_COMPONENTS = {
  Control,
  SingleValue,
  Input,
  ValueContainer,
  MultiValue,
  Option: OptionRow,
  MultiValueRemove,
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator: null,
}

const countOptions = (options: readonly unknown[] | undefined): number =>
  (options ?? []).reduce<number>((n, o) => {
    const group = o as { options?: readonly unknown[] }
    return n + (Array.isArray(group.options) ? group.options.length : 1)
  }, 0)

// No-results only renders once loading has finished: react-select shows the loading message while
// an async search is in flight.
const NoOptions = ({ inputValue }: { inputValue: string }) =>
  inputValue ? (
    <>
      <span className="text-fg-base">No results for “{inputValue}”</span>
      <span className="text-fg-subtle">Check the spelling or try a shorter search.</span>
    </>
  ) : (
    "No options"
  )

// react-select keeps disabled options focusable (hover shows their tooltip); arrow keys skip them.
const focusNextEnabled = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  select: SelectInstance<Option, IsMulti, Group>,
  step: 1 | -1,
): boolean => {
  if (typeof select.getFocusedOptionId !== "function" || !("focusedOption" in select.state))
    return false
  const options = select.getFocusableOptions()
  const value = select.getValue()
  if (!options.some((option) => !select.isOptionDisabled(option, value))) return true
  const current = select.state.focusedOption
  let index = current ? options.indexOf(current) : step === 1 ? -1 : 0
  do index = (index + step + options.length) % options.length
  while (select.isOptionDisabled(options[index]!, value))
  const next = options[index]!
  select.scrollToFocusedOptionOnUpdate = true
  select.setState({
    focusedOption: next,
    focusedValue: null,
    focusedOptionId: select.getFocusedOptionId(next),
  })
  return true
}

const filterOnLabel = createFilter<unknown>({
  stringify: (option) => {
    const { user } = asOption(option.data)
    return user ? `${option.label} ${user.email}` : option.label
  },
})

// Searchable once the list is long enough to scan (Figma: more than ~8 options).
const SEARCH_THRESHOLD = 8

const getClassNames = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  variant,
  size,
  isInvalid,
  isActive,
  modality,
  forceState,
  fullWidthMenu,
}: {
  variant: SelectVariant
  size: SelectSize
  isInvalid: boolean
  isActive: boolean
  modality: InputModality
  forceState: SelectProps<unknown, boolean>["forceState"]
  fullWidthMenu: boolean
}): ClassNamesConfig<Option, IsMulti, Group> => ({
  container: () => "w-full",
  control: ({ isFocused, isDisabled, menuIsOpen }) =>
    controlClass(
      variant,
      size,
      getControlState({
        isFocused,
        isDisabled,
        menuIsOpen,
        isInvalid,
        isActive,
        modality,
        forceState,
      }),
    ),
  valueContainer: () =>
    "relative flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden",
  placeholder: ({ isDisabled }) =>
    cn(
      size === "small" ? "paragraph-sm" : "paragraph-base",
      "truncate",
      isDisabled ? "text-fg-disabled" : "text-fg-muted",
    ),
  singleValue: ({ isDisabled }) =>
    cn(
      size === "small" ? "label-sm" : "label-base",
      accentText(isDisabled, isActive, "text-fg-base"),
    ),
  input: () => cn(size === "small" ? "label-sm" : "label-base", "text-fg-base"),
  multiValue: ({ isDisabled }) =>
    cn(
      chipClass(isDisabled, isActive),
      "data-hidden:pointer-events-none data-hidden:invisible data-hidden:absolute",
    ),
  multiValueLabel: () => "min-w-0 truncate",
  multiValueRemove: ({ isDisabled }) =>
    cn("flex rounded-[4px] text-fg-muted", !isDisabled && "cursor-pointer hover:text-fg-base"),
  indicatorsContainer: () => "flex shrink-0 items-center gap-1.5",
  clearIndicator: () =>
    cn(
      "flex cursor-pointer",
      isActive ? "text-fg-interactive" : "text-fg-muted hover:text-fg-subtle",
    ),
  dropdownIndicator: ({ isDisabled }) =>
    cn("flex", accentText(isDisabled, isActive, "text-fg-muted")),
  // react-select unmounts the menu on close, so only the entrance animates.
  menu: ({ placement }) =>
    cn(
      "mt-2 rounded-[12px] bg-base shadow-flyout overlay-enter",
      placement === "top"
        ? "mb-2 origin-bottom [--overlay-enter-y:4px]"
        : "mb-1 origin-top [--overlay-enter-y:-4px]",
      fullWidthMenu ? "w-full" : "min-w-full w-max! max-w-[360px]",
    ),
  menuList: () => "flex flex-col gap-0.5 p-1",
  group: () => "flex flex-col gap-0.5",
  groupHeading: () => "px-2 pt-2 pb-1 label-sm text-fg-subtle",
  option: ({ isDisabled, isFocused }) =>
    optionVariants({ size, state: getOptionState(isDisabled, isFocused) }),
  noOptionsMessage: ({ selectProps }) =>
    selectProps.inputValue
      ? // Wraps at the control's width instead of widening the menu to fit the hint.
        "flex flex-col gap-0.5 px-2 py-3 text-center paragraph-sm [contain:inline-size]"
      : "p-2 text-center paragraph-sm text-fg-subtle",
  loadingMessage: () => "p-2 text-center paragraph-sm text-fg-muted",
})

const getStyles = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  menuAlign: "left" | "right",
  firstChipMax: number | null,
): StylesConfig<Option, IsMulti, Group> => ({
  // `unstyled` still keeps react-select's layout styles (control min-height 38px, option
  // display/font-size/cursor, …). Emotion injects them after Tailwind, so at equal specificity
  // they beat our classes. Drop every key a class sets.
  control: (base) => omit(base, ["minHeight", "cursor", "flexWrap", "transition", "outline"]),
  valueContainer: (base) => omit(base, ["display", "flexWrap", "overflow", "alignItems"]),
  indicatorsContainer: (base) => omit(base, ["display", "alignItems", "flexShrink"]),
  multiValue: (base, { index }) => ({
    ...omit(base, ["display", "minWidth"]),
    ...(index === 0 && firstChipMax !== null ? { maxWidth: firstChipMax } : {}),
  }),
  multiValueRemove: (base) => omit(base, ["display", "alignItems"]),
  option: (base) => omit(base, ["cursor", "display", "fontSize", "width", "userSelect"]),
  groupHeading: (base) => omit(base, ["cursor", "display"]),
  // Classes cannot outrank the inline position react-select computes for the portal.
  menuPortal: (base) => ({ ...base, zIndex: 2147483647, pointerEvents: "auto" }),
  menu: (base) => ({
    ...omit(base, ["width"]),
    zIndex: 2147483647,
    // The portal is pinned to the control's left edge at the control's width. A menu wider than
    // the control grows rightward from there; anchoring its right edge grows it leftward instead.
    ...(menuAlign === "right" ? { right: 0, left: "auto" } : {}),
  }),
})

export type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<ReactSelectProps<Option, IsMulti, Group>, "classNames" | "unstyled" | "theme"> &
  Partial<
    Pick<AsyncProps<Option, IsMulti, Group>, "loadOptions" | "defaultOptions" | "cacheOptions">
  > &
  Partial<
    Pick<
      CreatableProps<Option, IsMulti, Group>,
      | "onCreateOption"
      | "formatCreateLabel"
      | "isValidNewOption"
      | "getNewOptionData"
      | "createOptionPosition"
    >
  > &
  PermissionGateProps & {
    /** `neutral` looks like the neutral Button; `ghost` is for toolbars and table headers. */
    variant?: SelectVariant
    /** `small` 24px (a control inline with 14px text, e.g. a list row's two-line cell), `base` 28px (tables, toolbars, filters) or `large` 32px (forms, settings). */
    size?: SelectSize
    width?: SelectWidth
    /** Field label above the control. Leave it off in toolbars and filters. */
    label?: string
    /** Helper text under the control. */
    description?: string
    /** Error text under the control. Also puts the control in its error state. */
    error?: string
    leftIcon?: ReactNode
    /** Short text before the value, e.g. "Group by:". Shown only once a value is set. */
    prefix?: string
    /** Adds a last `Create “…”` option for typed text that matches nothing. */
    isCreatable?: boolean
    /** Anchor the menu's left or right edge to the control. Use `right` near the screen edge. */
    menuAlign?: "left" | "right"
    /** Menu exactly as wide as the control, instead of at least as wide. */
    fullWidthMenu?: boolean
    /** Filter use: the value differs from the filter's default, so the control turns purple.
     * Neutral only, with a value. Defaults to `value !== defaultValue` when `defaultValue` is set. */
    isActive?: boolean
    /** Design-system docs only: pin the hover, open or focus look. */
    forceState?: "hover" | "open" | "focus"
  }

const isFixedOption = (option: unknown) => Boolean(asOption(option).isFixed)

const toList = <T,>(value: unknown): T[] => {
  if (value === null || value === undefined) return []
  return (Array.isArray(value) ? value : [value]) as T[]
}

/**
 * Pick one or more values from a list — single, multi, async, creatable and user pickers in one
 * component. Menu always portalled, at least control width, max 400px tall.
 * Canonical intent: {@link ../design-system/intents.ts SELECT_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export function Select<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  variant = "neutral",
  size = "base",
  width = "full",
  label,
  description,
  error,
  leftIcon,
  prefix,
  isCreatable,
  menuAlign = "left",
  fullWidthMenu = false,
  isActive,
  forceState,
  requires,
  permissionReason,
  allowedTitle,
  tooltipSide,
  wrapperClassName,
  ...props
}: SelectProps<Option, IsMulti, Group>) {
  const { blocked, gate } = usePermissionGate(
    { requires, permissionReason, allowedTitle, tooltipSide, wrapperClassName },
    WIDTH_CLASS[width],
  )
  const instanceId = useId()
  const inputId = props.inputId ?? `${instanceId}-input`
  const helperId = `${instanceId}-helper`
  const selectRef = useRef<SelectInstance<Option, IsMulti, Group>>(null)
  const [chipOverflow, setChipOverflow] = useState<ChipOverflow>(ALL_CHIPS)
  const inputModality = useInputModality()

  const isAsync = props.loadOptions !== undefined
  const creatable = isCreatable || props.onCreateOption !== undefined
  const isInvalid = Boolean(error) || Boolean(props["aria-invalid"])
  const isDisabled = Boolean(props.isDisabled) || blocked
  const helperText = error ?? description

  // Uncontrolled selects get their value mirrored here, for `isActive` and fixed values.
  const [uncontrolledValue, setUncontrolledValue] = useState(props.defaultValue)
  const currentValue = props.value !== undefined ? props.value : uncontrolledValue
  const getValue =
    props.getOptionValue ?? ((option: Option) => String((option as { value?: unknown }).value))
  const valueKey = (value: unknown) => toList<Option>(value).map(getValue).sort().join("\u0000")
  const hasValue = toList(currentValue).length > 0
  const active =
    variant === "neutral" &&
    hasValue &&
    (isActive ??
      (props.defaultValue !== undefined && valueKey(currentValue) !== valueKey(props.defaultValue)))

  // react-select opens on ArrowDown and Space but not Enter; buttons and menus open on all three.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    props.onKeyDown?.(event)
    if (event.defaultPrevented) return
    const menuOpen = Boolean(selectRef.current?.props.menuIsOpen)
    const arrow = { ArrowDown: 1, ArrowUp: -1 }[event.key] as 1 | -1 | undefined
    if (menuOpen && arrow && selectRef.current && !event.altKey && !event.metaKey) {
      if (focusNextEnabled(selectRef.current, arrow)) event.preventDefault()
      return
    }
    if (menuOpen || event.key !== "Enter") return
    event.preventDefault()
    selectRef.current?.openMenu("first")
  }

  // Fixed values are not removable: the X and unticking skip them, backspace removes the last
  // removable chip instead, clear keeps them.
  const handleChange = (value: OnChangeValue<Option, IsMulti>, meta: ActionMeta<Option>) => {
    if (meta.action === "clear" && props.defaultValue !== undefined)
      return emitChange(props.defaultValue as OnChangeValue<Option, IsMulti>, meta)
    if (!props.isMulti) return emitChange(value, meta)
    if (meta.action === "deselect-option" && isFixedOption(meta.option)) return
    if (meta.action === "remove-value" && isFixedOption(meta.removedValue)) return
    if (meta.action === "pop-value" && isFixedOption(meta.removedValue)) {
      const list = toList<Option>(currentValue)
      const removedValue = list.findLast((option) => !isFixedOption(option))
      if (removedValue === undefined) return
      const next = list.filter((option) => option !== removedValue)
      return emitChange(next as unknown as OnChangeValue<Option, IsMulti>, {
        ...meta,
        removedValue,
      })
    }
    if (meta.action === "clear") {
      const kept = meta.removedValues.filter(isFixedOption)
      return emitChange(kept as unknown as OnChangeValue<Option, IsMulti>, meta)
    }
    emitChange(value, meta)
  }

  function emitChange(value: OnChangeValue<Option, IsMulti>, meta: ActionMeta<Option>) {
    if (props.value === undefined) setUncontrolledValue(value)
    props.onChange?.(value, meta)
  }

  const Component = (() => {
    if (isAsync) return creatable ? AsyncCreatableSelect : AsyncSelect
    return creatable ? CreatableSelect : ReactSelect
  })() as unknown as typeof ReactSelect

  const injectedProps: InjectedProps = {
    leftIcon,
    prefix,
    isActive: active,
    helperId: helperText ? helperId : undefined,
    chipOverflow,
    onChipOverflow: setChipOverflow,
  }
  const picked = toList(currentValue)
  // Multi: clear shows from two picks, and only when it would remove something.
  const clearable = props.isMulti
    ? Boolean(props.isClearable) && picked.length >= 2 && !picked.every(isFixedOption)
    : Boolean(props.isClearable)

  const select = (
    // Stops the page scrolling while the wheel scrolls the menu.
    <div
      className={cn("flex flex-col gap-1.5", WIDTH_CLASS[width])}
      onWheel={(e) => e.stopPropagation()}
    >
      {label && (
        <label htmlFor={inputId} className="label-sm text-fg-subtle">
          {label}
        </label>
      )}
      <Component<Option, IsMulti, Group>
        unstyled
        ref={selectRef}
        instanceId={instanceId}
        // Fixed, so the menu never makes the page recompute its position on every render; a
        // portal, so no container can clip it.
        menuPortalTarget={document.body}
        menuPosition="fixed"
        maxMenuHeight={400}
        // Multi keeps the menu open and lists picked options with a check; clicking one unpicks
        // it. Tab leaves instead of picking the focused option.
        hideSelectedOptions={false}
        closeMenuOnSelect={!props.isMulti}
        tabSelectsValue={!props.isMulti}
        isSearchable={isAsync || creatable || countOptions(props.options) > SEARCH_THRESHOLD}
        cacheOptions={isAsync ? true : undefined}
        loadingMessage={() => "Loading…"}
        noOptionsMessage={({ inputValue }) => <NoOptions inputValue={inputValue} />}
        filterOption={isAsync ? null : filterOnLabel}
        formatCreateLabel={(input: string) => `Create “${input}”`}
        aria-errormessage={error ? helperId : undefined}
        {...props}
        {...injectedProps}
        // Always controlled underneath: the mirrored value is what lets fixed values survive.
        value={currentValue ?? null}
        // react-select turns clear on for every multi select; Figma shows it only on request.
        isClearable={clearable}
        inputId={inputId}
        aria-invalid={isInvalid || undefined}
        isDisabled={isDisabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        components={{ ...SELECT_COMPONENTS, ...props.components }}
        classNames={getClassNames<Option, IsMulti, Group>({
          variant,
          size,
          isInvalid,
          isActive: active,
          modality: inputModality,
          forceState,
          fullWidthMenu,
        })}
        styles={{
          ...getStyles<Option, IsMulti, Group>(menuAlign, chipOverflow.firstMax),
          ...props.styles,
        }}
      />
      {helperText && (
        <p
          id={helperId}
          className={cn("paragraph-sm", error ? "text-fg-negative-text" : "text-fg-subtle")}
        >
          {helperText}
        </p>
      )}
    </div>
  )

  return gate(select)
}

/** Maps users (e.g. `useGetUsers`) to options with an avatar, keyed by email. */
export const toUserOptions = (
  users: readonly { name: string; email: string; picture?: string | null }[],
): SelectOption[] =>
  users.map((user) => ({
    label: user.name || user.email,
    value: user.email,
    user: { name: user.name, email: user.email, picture: user.picture ?? undefined },
  }))

export type { MultiValue }
